import { useCallback, useState } from 'react';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { CheckboxValueType } from 'antd/es/checkbox/Group';

import { InviteHeader } from 'components/ClientsDrawer/Headers';
import FormTitle from 'components/FormTitle';
import Footer from 'components/ClientsDrawer/Footer';
import RadioOptions from 'components/Form/RadioOptions';
import { OptionGroup } from 'pages/Settings/Notifications/components/OptionGroup';

import {
  getActiveClientPreApproval,
  getActiveClientTransactionInstance,
  getInvitedClientValues,
} from 'store/selectors/app';
import { inviteClientSteps, YesNoOptions } from 'types/inviteClient';
import { NotificationSubVariants, mapItemTitles } from 'settings/constants/notifications';
import {
  firstStepInputGroup,
  getInitialValues,
  getTheFirstStepOptions,
  getTheSecondStepOptions,
} from './helpers';

import styles from './styles.module.scss';
import { cloneDeep, isEqual } from 'lodash-es';
import { getIsAdminOrOwnerSelector } from 'store/selectors/agentTeamDetail';
import { AGENT } from 'settings/constants/roles';
import {
  appSetInviteClientNotificationValuesAction,
  appSetInviteClientValuesAction,
  appSetShowAssignConnectionAction,
} from 'store/actions/app';

const enableNotificationsOptions = [
  {
    id: YesNoOptions.Yes,
    label: YesNoOptions.Yes,
    value: YesNoOptions.Yes,
  },
  {
    id: YesNoOptions.No,
    label: YesNoOptions.No,
    value: YesNoOptions.No,
  },
];

export const InviteClientNotifications = ({ onNext, stageIndex, onPrev, onSubmit }: any) => {
  const dispatch = useDispatch();
  const [trigger, setTrigger] = useState(false);
  const { firstName } = useSelector(getInvitedClientValues);
  const activeClientTransactionInstance = useSelector(getActiveClientTransactionInstance);
  const {
    isPreApproved: isClientPreApproved,
    isManualDataInput,
    ...activeClientPreApproval
  } = useSelector(getActiveClientPreApproval);
  const isAdminOrOwner = useSelector(getIsAdminOrOwnerSelector);

  const handleSubmit = useCallback(
    (values) => {
      dispatch(appSetInviteClientNotificationValuesAction(values));
      if (isAdminOrOwner) {
        dispatch(
          appSetShowAssignConnectionAction({
            open: false,
            assignType: AGENT,
            newInvite: true,
          }),
        );
        onNext(inviteClientSteps.ClientConnections);
      } else {
        onSubmit({ onNext, clientNotificationValues: values });
        onNext(inviteClientSteps.ClientInvite);
      }
    },
    [activeClientTransactionInstance, activeClientPreApproval, isClientPreApproved],
  );

  const formik = useFormik({
    initialValues: getInitialValues(firstName),
    enableReinitialize: true,
    onSubmit: handleSubmit,
  });

  const handleIsEnabledChange = useCallback(
    (value) => {
      formik.setFieldValue('isEnabled', value);
    },
    [formik.setFieldValue],
  );

  const handleVariantChange = useCallback(
    ({ notificationItemIndex, checkedValues }) => {
      const notificationPreferences = JSON.parse(
        JSON.stringify(formik.values.NotificationPreferences),
      );
      const group = notificationPreferences[0];
      const notificationItem = group.NotificationItems[notificationItemIndex];
      const variants = notificationItem.Variants;

      Object.keys(variants).forEach((variant) => {
        variants[variant] = !!checkedValues.includes(variant);
      });

      if (checkedValues?.length) {
        const setRealTimeValue =
          notificationItem?.SubVariants?.some(({ Key }) => Key === 'Realtime') &&
          notificationItem?.SubVariants?.every(({ Checked }) => Checked === false);
        const setSecondRealTimeValue =
          notificationItem?.SecondSubVariants?.some(({ Key }) => Key === 'Realtime') &&
          notificationItem?.SecondSubVariants?.every(({ Checked }) => Checked === false);
        if (notificationItem.SubVariants?.[0]) notificationItem.SubVariants[0].Checked = true;
        setTrigger((prev) => !prev);
        if (setRealTimeValue) {
          const Realtime = notificationItem?.SubVariants?.find(({ Key }) => Key === 'Realtime');
          Realtime.Checked = true;
        }
        if (setSecondRealTimeValue) {
          const RealtimeSecond = notificationItem?.SecondSubVariants?.find(
            ({ Key }) => Key === 'Realtime',
          );
          RealtimeSecond.Checked = true;
        }
      }

      formik.setFieldValue('NotificationPreferences', notificationPreferences);
    },
    [formik.values.NotificationPreferences, formik.setFieldValue],
  );

  const handleChangeSubVariant = useCallback(
    ({ notificationItemIndex, checkedValues, inputValue, selectedValue }) => {
      const notificationPreferences = JSON.parse(
        JSON.stringify(formik.values.NotificationPreferences),
      );
      const group = notificationPreferences[0];
      const notificationItem = group.NotificationItems[notificationItemIndex];
      const subVariants = notificationItem.SubVariants;
      const newSubVariants = subVariants.map((subVariant) => ({
        ...subVariant,
        ...(checkedValues ? { Checked: checkedValues.includes(subVariant.Key) } : {}),
        ...(inputValue !== null && checkedValues.includes(subVariant.Key)
          ? { Value: inputValue }
          : {}),
        ...(selectedValue && checkedValues.includes(subVariant.Key)
          ? { SelectedValue: selectedValue }
          : {}),
      }));

      notificationItem.SubVariants = newSubVariants;

      formik.setFieldValue('NotificationPreferences', notificationPreferences);
    },
    [formik.values.NotificationPreferences, formik.setFieldValue],
  );

  const handleChangeSecondSubVariant = useCallback(
    ({ checkedValues: values, inputValue, notificationItemIndex }) => {
      const notificationPreferences = JSON.parse(
        JSON.stringify(formik.values.NotificationPreferences),
      );
      const group = notificationPreferences[0];
      const notificationItem = group.NotificationItems[notificationItemIndex];

      let checkedValues = cloneDeep(values);
      if (values.length > 1 && isEqual(values, ['Realtime', 'OnceDay'])) {
        const old = notificationItem.SecondSubVariants?.filter((opt) => opt.Checked).map(
          (opt) => opt.Key,
        );
        checkedValues = checkedValues.filter((chk) => !old.includes(chk));
      }

      const secondSubVariants = notificationItem.SecondSubVariants;
      const newSecondSubVariants = secondSubVariants.map((secondSubVariant) => ({
        ...secondSubVariant,
        ...(checkedValues ? { Checked: checkedValues.includes(secondSubVariant.Key) } : {}),
        ...(inputValue !== null &&
        inputValue !== undefined &&
        checkedValues.includes(secondSubVariant.Key)
          ? { Value: inputValue }
          : {}),
      }));

      notificationItem.SecondSubVariants = newSecondSubVariants;

      formik.setFieldValue('NotificationPreferences', notificationPreferences);
    },
    [formik.values.NotificationPreferences, formik.setFieldValue],
  );
  return (
    <form onSubmit={formik.handleSubmit} className={styles.notificationsForm}>
      <InviteHeader stageIndex={stageIndex} onPrev={onPrev} isValid={formik.isValid} />
      <div className={styles.formContainer}>
        <FormTitle
          className={styles.title}
        >{`Do you want to turn on property notifications for ${firstName}?`}</FormTitle>
        <RadioOptions
          options={enableNotificationsOptions}
          name="isEnabled"
          onChange={handleIsEnabledChange}
          value={formik.values.isEnabled}
          testid="is_enabled"
          className={styles.selectType}
          optionClassName={styles.selectOption}
          checkedClassName={styles.selectChecked}
          labelTextClassName={styles.labelText}
        />
        {formik.values.isEnabled === YesNoOptions.Yes && (
          <div className={styles.notificationItemsWrapper}>
            {formik.values.NotificationPreferences[0].NotificationItems.map(
              (notificationItem, notificationItemIndex) => (
                <OptionGroup
                  trigger={trigger}
                  fieldTitle={notificationItem.Text}
                  firstStepBoxTitle={(notificationItem as any).SubText || ''}
                  secondStepBoxTitle={notificationItem.SecondSubText}
                  key={notificationItem.Types}
                  options={Object.keys(notificationItem.Variants).map((key) => {
                    return {
                      label: mapItemTitles.PerType[key]().Title,
                      value: key,
                      checked: notificationItem.Variants[key],
                    };
                  })}
                  hasFirstStep={!!notificationItem.SubVariants?.length}
                  hasSecondStep={!!notificationItem.SecondSubVariants?.length}
                  isFirstStepInputGroup={
                    !!notificationItem.SubVariants?.filter((subVar) =>
                      firstStepInputGroup.includes(subVar.Key as NotificationSubVariants),
                    ).length
                  }
                  isFirstStepDropDownGroup={
                    !!notificationItem.SubVariants?.filter(
                      (subVar) => subVar.Key === NotificationSubVariants.QuoteRequestRemind,
                    ).length
                  }
                  isSecondStepNumberGroup={
                    !!notificationItem.SecondSubVariants?.filter(
                      (secondSubVar) => secondSubVar.Key === NotificationSubVariants.Realtime,
                    ).length
                  }
                  firstStepOptions={getTheFirstStepOptions(notificationItem)}
                  secondStepOptions={getTheSecondStepOptions(notificationItem)}
                  onMainCheckboxChange={(checkedValues: CheckboxValueType[]) =>
                    handleVariantChange({ checkedValues, notificationItemIndex })
                  }
                  onStepOneCheckboxChange={(checkedValues, inputValue, selectedValue) =>
                    handleChangeSubVariant({
                      checkedValues,
                      inputValue,
                      selectedValue,
                      notificationItemIndex,
                    })
                  }
                  onStepTwoCheckboxChange={(
                    checkedValues: CheckboxValueType[],
                    inputValue: number,
                  ) =>
                    handleChangeSecondSubVariant({
                      checkedValues,
                      inputValue,
                      notificationItemIndex,
                    })
                  }
                />
              ),
            )}
          </div>
        )}
      </div>
      <Footer
        buttonType="submit"
        buttonTitle={isAdminOrOwner ? 'Continue' : 'Send Invitation'}
        isValid={formik.isValid}
        testid="invite_buyer_footer_notifications"
      />
    </form>
  );
};
